import { useTranslation } from 'react-i18next';
import { type ModuleSpecsFragment } from '../../../../api/fragments/ModuleSpecs';
import { ConditionType, ModuleType } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import BankSelectField from '../../../../components/fields/BankSelectField';
import DealerSelectField from '../../../../components/fields/DealerSelectField';
import InsurerSelectField from '../../../../components/fields/InsurerSelectField';
import LocationSelectField from '../../../../components/fields/LocationSelectField';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import LogicConditionFieldArray from './LogicConditionFieldArray';

export type ConditionsCardProps = {
    conditionType: ConditionType;
    prefix: string;
    module?: ModuleSpecsFragment;
};

const ConditionsCard = ({ module, conditionType, prefix }: ConditionsCardProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const company = useCompany(true);

    const renderLogicConditionForm = (conditionType: ConditionType) => (
        <LogicConditionFieldArray
            labelForAdd={t('consentsAndDeclarations:addLogicCondition', { conditionType })}
            name={`${prefix}.logicConditionSettings.children`}
        />
    );

    const renderApplicationModuleConditionForm = () => (
        <ModuleSelectField
            {...t('consentsAndDeclarations:fields.applicationModule', {
                returnObjects: true,
            })}
            moduleTypes={[
                ModuleType.StandardApplicationModule,
                ModuleType.EventApplicationModule,
                ModuleType.ConfiguratorModule,
                ModuleType.FinderApplicationPublicModule,
                ModuleType.FinderApplicationPrivateModule,
            ]}
            name={`${prefix}.applicationModuleConditionSettings.moduleId`}
            required
        />
    );

    const renderBankConditionForm = () => (
        <BankSelectField
            {...t('consentsAndDeclarations:fields.bank', {
                returnObjects: true,
            })}
            companyId={company?.id}
            name={`${prefix}.bankConditionSettings.bankId`}
            required
        />
    );

    const renderDealerConditionForm = () => (
        <DealerSelectField
            {...t('consentsAndDeclarations:fields.dealer', {
                returnObjects: true,
            })}
            companyId={company?.id}
            name={`${prefix}.dealerConditionsSettings.dealerId`}
            required
        />
    );

    const renderInsurerConditionForm = () => (
        <InsurerSelectField
            {...t('consentsAndDeclarations:fields.insurer', {
                returnObjects: true,
            })}
            companyId={company?.id}
            name={`${prefix}.insurerConditionSettings.insurerId`}
            required
        />
    );

    const renderLocationConditionForm = () => (
        <LocationSelectField
            {...t('consentsAndDeclarations:fields.location', {
                returnObjects: true,
            })}
            companyId={company?.id}
            module={module}
            name={`${prefix}.locationConditionSettings.locationId`}
            parentName={`${prefix}.locationConditionSettings`}
            required
        />
    );

    const renderGiftVoucherConditionForm = () => (
        <ModuleSelectField
            {...t('consentsAndDeclarations:fields.giftVoucherModule', {
                returnObjects: true,
            })}
            moduleTypes={[ModuleType.GiftVoucherModule]}
            name={`${prefix}.giftVoucherConditionSettings.moduleId`}
            required
        />
    );

    switch (conditionType) {
        case ConditionType.Or:
            return renderLogicConditionForm(conditionType);

        case ConditionType.IsApplicationModule:
            return renderApplicationModuleConditionForm();

        case ConditionType.IsBank:
            return renderBankConditionForm();

        case ConditionType.IsDealer:
            return renderDealerConditionForm();

        case ConditionType.IsInsurer:
            return renderInsurerConditionForm();

        case ConditionType.IsLocation:
            return renderLocationConditionForm();

        case ConditionType.IsGiftVoucher:
            return renderGiftVoucherConditionForm();

        default:
            return null;
    }
};

export default ConditionsCard;
